/** CRUD de destaques com atualização otimista (Fase 03 US-05).
 *
 * Otimista porque marcar um trecho tem de parecer instantâneo: esperar o
 * *round-trip* numa rede de escola faria o aluno clicar de novo, e clicar de
 * novo é como nasce o destaque duplicado — que é justamente o que o `client_id`
 * existe para evitar do lado do servidor.
 *
 * `disponivel: false` quando o endpoint responde 404: permite a tela do leitor
 * funcionar contra um backend que ainda não subiu a migração, escondendo o botão
 * em vez de mostrar erro a cada abertura de livro.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { problemDetail, problemStatus } from '@/services/api'
import {
  createHighlight,
  deleteHighlight,
  fetchHighlights,
} from '@/services/readerService'
import type { CorDestaque, Highlight, HighlightCreate, Rect } from '@/types/api'

export const CORES: { valor: CorDestaque; rotulo: string }[] = [
  { valor: 'yellow', rotulo: 'amarelo' },
  { valor: 'green', rotulo: 'verde' },
  { valor: 'blue', rotulo: 'azul' },
  { valor: 'pink', rotulo: 'rosa' },
]

interface Dependencias {
  buscar?: (bookId: string) => Promise<Highlight[]>
  criar?: (payload: HighlightCreate) => Promise<Highlight>
  remover?: (id: string) => Promise<void>
  /** Injetável para teste: `crypto.randomUUID` não é determinístico. */
  novoId?: () => string
}

export interface NovoDestaque {
  note?: string
  client_id?: string
  page: number
  rect: Rect
  rects: Rect[]
  text_content: string
  color: CorDestaque
}

export function useHighlights(bookId: string | null, deps: Dependencias = {}) {
  const {
    buscar = fetchHighlights,
    criar = createHighlight,
    remover = deleteHighlight,
    novoId = () => crypto.randomUUID(),
  } = deps

  const queryClient = useQueryClient()
  const chave = ['highlights', bookId]

  const query = useQuery({
    queryKey: chave,
    queryFn: () => buscar(bookId!),
    enabled: bookId !== null,
    retry: false,
  })

  const criarMutation = useMutation({
    mutationFn: (entrada: NovoDestaque) =>
      criar({
        book_id: bookId!,
        location_type: 'pdf_rect',
        page: entrada.page,
        rect: entrada.rect,
        rects: entrada.rects,
        text_content: entrada.text_content,
        color: entrada.color,
        client_id: entrada.client_id ?? novoId(),
        ...(entrada.note !== undefined ? { note: entrada.note } : {}),
      }),
    onMutate: async (entrada) => {
      await queryClient.cancelQueries({ queryKey: chave })
      const anterior = queryClient.getQueryData<Highlight[]>(chave) ?? []
      const provisorio: Highlight = {
        id: `tmp-${novoId()}`,
        book_id: bookId!,
        location_type: 'pdf_rect',
        page: entrada.page,
        xpath_start: null,
        xpath_end: null,
        rect: entrada.rect,
        rects: entrada.rects,
        text_content: entrada.text_content,
        note: entrada.note ?? null,
        color: entrada.color,
        client_id: null,
        created_at: new Date().toISOString(),
      }
      queryClient.setQueryData<Highlight[]>(chave, [provisorio, ...anterior])
      return { anterior }
    },
    onError: (_erro, _entrada, contexto) => {
      queryClient.setQueryData<Highlight[]>(chave, contexto?.anterior ?? [])
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: chave }),
  })

  const removerMutation = useMutation({
    mutationFn: (id: string) => remover(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: chave })
      const anterior = queryClient.getQueryData<Highlight[]>(chave) ?? []
      queryClient.setQueryData<Highlight[]>(
        chave,
        anterior.filter((h) => h.id !== id),
      )
      return { anterior }
    },
    onError: (_erro, _id, contexto) => {
      queryClient.setQueryData<Highlight[]>(chave, contexto?.anterior ?? [])
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: chave }),
  })

  const destaques = useMemo(() => query.data ?? [], [query.data])

  const porPagina = useMemo(() => {
    const mapa = new Map<number, Highlight[]>()
    for (const h of destaques) {
      if (h.page === null) continue
      const lista = mapa.get(h.page) ?? []
      lista.push(h)
      mapa.set(h.page, lista)
    }
    return mapa
  }, [destaques])

  return {
    destaques,
    porPagina,
    criar: (entrada: NovoDestaque) => criarMutation.mutate({ ...entrada, client_id: entrada.client_id ?? novoId() }),
    criarAsync: (entrada: NovoDestaque) => criarMutation.mutateAsync({ ...entrada, client_id: entrada.client_id ?? novoId() }),
    remover: removerMutation.mutate,
    salvando: criarMutation.isPending || removerMutation.isPending,
    erro: criarMutation.isError ? (problemDetail(criarMutation.error) ?? 'Não foi possível salvar a marcação. Seu destaque não foi confirmado.')
      : removerMutation.isError ? 'Não foi possível apagar a marcação. Ela foi mantida.'
      : query.isError ? 'Não foi possível carregar suas anotações e destaques.' : null,
    tentarNovamente: () => {
      if (criarMutation.isError && criarMutation.variables) criarMutation.mutate(criarMutation.variables)
      else if (removerMutation.isError && removerMutation.variables) removerMutation.mutate(removerMutation.variables)
      else void query.refetch()
    },
    /** 404 = backend sem o endpoint ainda; esconder é melhor que errar sempre. */
    disponivel: !(query.isError && problemStatus(query.error) === 404),
    carregando: query.isLoading,
  }
}
